import { api, readTotalCount } from '../api/client';
import { API_ITEM_FOLLOW_URL, API_ITEM_FOLLOWS_URL } from '../utils';

// Following one item (keepr-api docs/SCHEMA.md § Following an item; KPR-391):
// "tell me when this changes". A person who can read an item follows it, and a
// change to one of its card's followable elements reaches them in the inbox —
// and by mail unless they turned that off — for as long as they can still
// read it. Every route is session-only; errors propagate, and each caller
// says its own words through `followRefusal`.

/** `{ followed, canFollow, reason?, elements: [{ name, label }] }` for one item. */
export async function getItemFollow(itemId) {
	const { data } = await api.get(API_ITEM_FOLLOW_URL(itemId));
	return data || null;
}

export async function followItem(itemId) {
	const { data } = await api.post(API_ITEM_FOLLOW_URL(itemId));
	return data;
}

/** Idempotent, and it reads nothing of the item: a follower who lost access can still leave. */
export async function unfollowItem(itemId) {
	const { data } = await api.delete(API_ITEM_FOLLOW_URL(itemId));
	return data;
}

/**
 * `{ rows, total }` — the person's follows, newest first. A row they can read
 * now carries `title`, `card` and `collection`; one they cannot is
 * `{ item_id, followedAt, available: false }` and nothing else.
 */
export async function listItemFollows({ skip = 0, limit = 50 } = {}) {
	const { data, headers } = await api.get(API_ITEM_FOLLOWS_URL, { params: { skip, limit } });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

/**
 * The sentence a person reads for why they cannot follow — from a refusal, or
 * from the `reason` GET …/follow already gives (the same codes).
 */
export function followReasonText(code, fallback = 'This item cannot be followed.') {
	switch (code) {
		case 'follow_not_allowed': return 'The owner of this collection does not allow follows.';
		case 'collection_retiring': return 'This collection is retiring, so its items can no longer be followed.';
		case 'item_not_followable': return 'Nothing on this card is followed. A manager can turn on "Tell followers when this changes" for an element.';
		case 'follow_needs_access': return 'You can open this item as an admin, but you have no access of your own to it, so you would never be told about it.';
		case 'follow_limit': return 'You follow as many items as one account can (500). Unfollow some in Settings › Notifications first.';
		default: return fallback;
	}
}

export function followRefusal(error, fallback = 'That did not work. Trying again may help.') {
	const body = error?.response?.data || {};
	if (error?.response?.status === 429) return 'Too many follows in a minute. Try again shortly.';
	if (error?.response?.status === 404) return 'This item is no longer available to you.';
	return followReasonText(body.code, body.message || fallback);
}

/** "Status" · "Status and Due" · "Status, Due and Fixed" — what a follower hears about. */
export function followedElementsText(elements) {
	const labels = (Array.isArray(elements) ? elements : []).map(e => e && (e.label || e.name)).filter(Boolean);
	if (!labels.length) return '';
	if (labels.length === 1) return labels[0];
	return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
